import { PortalLocationShare } from "../components/PortalLocationShare";
import { sendLocation } from "../lib/riderLocation";
import { useEffect, useState, useCallback, useRef } from "react";
import { RiderSalaryPreview } from "../components/RiderSalaryPreview";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  LogOut, ChevronDown, KeyRound, LogIn, LogOut as OutIcon, CalendarClock,
  FileText, Boxes, User, Clock, CheckCircle2, AlertTriangle, XCircle, Wallet, Loader2, Building2, Camera,
  Plus, FileSignature, Printer, Eye, Bell, Wallet as WalletIcon, TrendingUp, Paperclip, Fuel, MapPin, Navigation,
  Banknote, MessageSquareWarning, Check, X, ExternalLink, Gift, Crown, Trophy, Share2, Receipt, Menu, Sparkles, CreditCard, Droplets, Wrench, HelpCircle, PhoneCall, HeartPulse, ClipboardList, CarFront } from "lucide-react";
import { ZoomableView } from "../components/FilePreviewTools";
import { api, fileUrl } from "../lib/api";
import { itemsFor, CarDamageDiagram } from "../components/VehicleDamageDiagram";
import { printReceipt, printVehicleReceipt } from "../lib/custodyReceipt";
import { useAuth } from "../context/AuthContext";
import { PasskeySettingCard } from "../components/PasskeySettingCard";
import { useLang } from "../i18n/LanguageContext";
import { ChangePasswordDialog } from "../components/ChangePasswordDialog";
import { ImageZoom } from "../components/ImageZoom";
import { TargetWarningGate } from "../components/TargetWarningGate";
import { SignatureConsentGate } from "../components/SignatureConsentGate";
import { SignaturePad } from "../components/SignaturePad";
import { PerformanceTab } from "../components/EmployeePerfPanels";
import { LeaveBoard } from "../components/LeaveBoard";
import { PortalLoyalty } from "../components/PortalLoyalty";
import { PortalPurchases } from "../components/PortalPurchases";
import { PortalWallet } from "../components/PortalWallet";
import { PortalRiderCash } from "../components/PortalRiderCash";
import { PortalAccidents } from "../components/PortalAccidents";
import { PortalKeetaViolations } from "../components/PortalKeetaViolations";
import { PortalTeamAttendance } from "../components/PortalTeamAttendance";
import { shareAchievement } from "../lib/shareAchievement";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { PushToggle } from "../components/PushToggle";
import { InstallAppButton } from "../components/InstallAppButton";
import { AnnouncementTicker } from "../components/AnnouncementTicker";
import { PortalInjuries } from "../components/PortalInjuries";
import { PortalTasks } from "../components/PortalTasks";
import { PortalDecisions, PortalLibrary } from "../components/PortalDecisions";
import { PortalFlights } from "../components/PortalFlights";
import { PortalLegalSummons } from "../components/PortalLegalSummons";
import { PortalResignation } from "../components/PortalResignation";
import { Gavel, Library as LibraryIcon, MessagesSquare as MessagesSquareIcon } from "lucide-react";
import { PortalChat } from "../components/PortalChat";
import { PortalRiderCard } from "../components/PortalRiderCard";
import { IdCard as IdCardIcon } from "lucide-react";
import { Fingerprint } from "lucide-react";
import { attendanceBio, optionalBio } from "../lib/passkey";
import { PortalPhoneCard, PhoneReminder } from "../components/PortalPhoneCard";
import { Textarea } from "../components/ui/textarea";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { issueAndPrintFreelance } from "../lib/freelanceInvoice";
import { useBackNav } from "../hooks/use-back-nav";
import { setAutoOrg } from "../lib/autoTranslate";

const STATUS_UI = {
  valid: { c: "text-emerald-600", bg: "bg-emerald-50 border-emerald-100", Icon: CheckCircle2 },
  expiring_soon: { c: "text-amber-600", bg: "bg-amber-50 border-amber-100", Icon: AlertTriangle },
  expired: { c: "text-red-600", bg: "bg-red-50 border-red-100", Icon: XCircle },
  none: { c: "text-slate-400", bg: "bg-slate-50 border-slate-200", Icon: FileText },
};

const ATT_COLOR = { present: "text-emerald-600", absent: "text-red-600", late: "text-amber-600", leave: "text-blue-600", permission: "text-violet-600" };

const FuelCapCard = ({ total, fc, cur }) => {
  const pct = Math.min(100, fc.cap ? (total / fc.cap) * 100 : 100);
  const over = Math.max(0, total - fc.cap);
  const bar = pct >= 100 ? "bg-rose-500" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="space-y-2 rounded-2xl border border-slate-200 bg-white p-4" data-testid="portal-fuel-cap">
      <div className="flex items-center justify-between text-sm"><span className="font-semibold">شريحة البنزين</span>
        <span className="font-mono-nums" dir="ltr">{total.toLocaleString("en-US")} / {fc.cap.toLocaleString("en-US")} {cur}</span></div>
      <div className="h-2.5 overflow-hidden rounded-full bg-slate-100"><div className={`h-full ${bar} transition-all duration-700`} style={{ width: `${pct}%` }} /></div>
      <p className={`text-xs ${over ? "font-semibold text-rose-600" : "text-slate-500"}`} data-testid="portal-fuel-cap-status">
        {over ? `تجاوزت الشريحة بـ ${over.toLocaleString("en-US")} ${cur} — تُخصم من راتبك` : `المتبقي من الشريحة ${(fc.cap - total).toLocaleString("en-US")} ${cur}`}
      </p>
      {fc.target_orders > 0 && <p className="text-[11px] text-amber-700">تنبيه: إذا لم تحقق التارقت ({fc.target_orders} طلب) تتحمّل كامل مصروف البنزين.</p>}
    </div>
  );
};

const OBJECTION_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLSetHDXpFlMausygfzuAdDLz6zI8i8zGikANZQkxFbbXqQTAcQ/viewform";
const PORTAL_TABS = [
  { value: "profile", Icon: User },
  { value: "docs", Icon: FileText },
  { value: "custody", Icon: Boxes },
  { value: "advances", Icon: Wallet },
  { value: "wallet", Icon: WalletIcon },
  { value: "cash", Icon: Banknote },
  { value: "performance", Icon: TrendingUp },
  { value: "violations", Icon: AlertTriangle },
  { value: "injuries", Icon: HeartPulse },
  { value: "accidents", Icon: CarFront },
  { value: "attendance", Icon: Clock },
  { value: "fuel", Icon: Fuel },
  { value: "maintenance", Icon: Wrench },
  { value: "salary", Icon: Banknote },
  { value: "leave", Icon: CalendarClock },
  { value: "loyalty", Icon: Gift },
  { value: "purchases", Icon: Receipt },
  { value: "mycard", Icon: IdCardIcon },
  { value: "chat", Icon: MessagesSquareIcon },
  { value: "decisions", Icon: Gavel },
  { value: "library", Icon: LibraryIcon },
  { value: "faq", Icon: HelpCircle },
  { value: "contacts", Icon: PhoneCall },
];

const ADV_STATUS_CLS = {
  requested: "bg-amber-100 text-amber-700",
  approved: "bg-blue-100 text-blue-700",
  signed: "bg-violet-100 text-violet-700",
  disbursed: "bg-emerald-100 text-emerald-700",
  rejected: "bg-rose-100 text-rose-700",
};
const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const officialSigBlock = (o) => {
  o = o || {};
  const base = process.env.REACT_APP_BACKEND_URL + "/api/files";
  const sig = o.signature_id
    ? `<img src="${base}/${o.signature_id}" style="height:${o.sig_size || 140}px;max-width:340px;object-fit:contain"/>`
    : `<div style="width:200px;height:64px;border-bottom:1px solid #94a3b8"></div>`;
  const stamp = o.stamp_id
    ? `<div style="margin-top:8px"><img src="${base}/${o.stamp_id}" style="height:${o.stamp_size || 190}px;max-width:340px;object-fit:contain"/></div>`
    : "";
  const title = esc(o.official_title || "المسؤول");
  const name = esc(o.official_name || "");
  return `<div style="text-align:center;min-width:200px"><div style="min-height:90px;display:flex;align-items:flex-end;justify-content:center">${sig}</div><div style="border-top:1px solid #94a3b8;margin-top:3px;padding-top:5px;font-size:13px;font-weight:700;color:#334155">${title}${name ? `: ${name}` : ""}</div>${stamp}<div style="font-size:10px;color:#64748b;margin-top:3px">توقيع المسؤول والختم</div></div>`;
};

const printPortalReceipt = (a, companyName, empName, sigUrl, L, official) => {
  const w = window.open("", "_blank");
  if (!w) return;
  const money = (n) => `${Number(n || 0).toLocaleString("en-US")} ${L.cur}`;
  const sig = sigUrl ? `<img src="${sigUrl}" style="max-height:90px" crossorigin="use-credentials"/>` : `<i>${L.noSig}</i>`;
  w.document.write(`<!doctype html><html dir="${L.dir}" lang="${L.lang}"><head><style>@font-face{font-family:SaudiRiyal;src:url(/fonts/riyal.woff2) format("woff2");unicode-range:U+20C1}</style><meta charset="utf-8"><title>${L.title}</title>
  <style>body{font-family:SaudiRiyal,Tahoma,Arial;padding:32px;color:#0f172a}h1{font-size:22px;margin:0 0 4px}
  .box{max-width:640px;margin:auto;border:1px solid #e2e8f0;border-radius:14px;padding:26px}
  table{width:100%;border-collapse:collapse;margin:16px 0}td{padding:9px 6px;border-bottom:1px solid #eef2f7;font-size:15px}
  .lbl{color:#64748b;width:40%}.amt{font-size:26px;font-weight:800;color:#2563eb}
  .sig{margin-top:20px;border-top:1px dashed #cbd5e1;padding-top:12px}</style></head><body>
  <div class="box"><h1>${L.heading}</h1><div style="color:#64748b;font-size:13px">${esc(companyName) || "WorkZone"}</div>
  <div class="amt" style="margin:14px 0">${money(a.amount)}</div>
  <table>
    <tr><td class="lbl">${L.empName}</td><td>${esc(empName) || "—"}</td></tr>
    <tr><td class="lbl">${L.reason}</td><td>${esc(a.reason) || "—"}</td></tr>
    <tr><td class="lbl">${L.approvedDate}</td><td>${(a.approved_at || "").slice(0, 10) || "—"}</td></tr>
    <tr><td class="lbl">${L.signedDate}</td><td>${(a.signed_at || "").slice(0, 10) || "—"}</td></tr>
    <tr><td class="lbl">${L.disbursedDate}</td><td>${(a.disbursed_at || "").slice(0, 10) || "—"}</td></tr>
  </table>
  <div class="sig" style="display:flex;justify-content:space-between;align-items:flex-end;gap:20px"><div><div style="color:#64748b;font-size:13px">${L.sigLabel}</div>${sig}</div>${officialSigBlock(official)}</div>
  <p style="color:#94a3b8;font-size:12px;margin-top:18px">${L.declaration}</p>
  </div><script>window.onload=()=>setTimeout(()=>window.print(),400)</script></body></html>`);
  w.document.close();
};

const printPayslip = (rec, fields, companyName, empName, L, official) => {
  const w = window.open("", "_blank");
  if (!w) return;
  const v = rec.values || {};
  const m = (n) => `${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })} ${L.cur}`;
  const adds = fields.filter((f) => ["base", "allow"].includes(f.group));
  const deds = fields.filter((f) => f.group === "deduct");
  const rowsHtml = (arr) => arr.map((f) => `<tr><td class="lbl">${esc(f.label)}</td><td class="val">${m(v[f.key])}</td></tr>`).join("");
  w.document.write(`<!doctype html><html dir="${L.dir}" lang="${L.lang}"><head><style>@font-face{font-family:SaudiRiyal;src:url(/fonts/riyal.woff2) format("woff2");unicode-range:U+20C1}</style><meta charset="utf-8"><title>${L.title}</title>
  <style>body{font-family:SaudiRiyal,Tahoma,Arial;padding:28px;color:#0f172a;background:#fff}
  .box{max-width:760px;margin:auto;border:1px solid #e2e8f0;border-radius:16px;padding:28px}
  .hd{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #1e40af;padding-bottom:14px;margin-bottom:16px}
  .hd h1{font-size:20px;margin:0;color:#1e40af}.hd .co{font-size:15px;font-weight:700}
  .meta{color:#64748b;font-size:13px;margin-top:4px}
  .cols{display:flex;gap:18px;flex-wrap:wrap}.col{flex:1;min-width:280px}
  .col h3{font-size:14px;margin:0 0 6px;color:#334155}
  table{width:100%;border-collapse:collapse}td{padding:7px 6px;border-bottom:1px solid #eef2f7;font-size:13px}
  .lbl{color:#475569}.val{text-align:left;font-weight:600;direction:ltr}
  .deds .val{color:#e11d48}
  .totals{margin-top:18px;display:flex;gap:14px}
  .tot{flex:1;border-radius:12px;padding:14px;text-align:center}
  .tot.g{background:#eff6ff;color:#1d4ed8}.tot.n{background:#ecfdf5;color:#047857}
  .tot .lab{font-size:12px}.tot .num{font-size:22px;font-weight:800;direction:ltr}
  .ftr{color:#94a3b8;font-size:11px;margin-top:20px;text-align:center}</style></head><body>
  <div class="box">
    <div class="hd"><div><h1>${L.title}</h1><div class="meta">${L.month} ${esc(rec.month) || "—"}</div></div>
      <div style="text-align:left"><div class="co">${esc(companyName) || "WorkZone"}</div><div class="meta">${esc(empName || rec.employee_name) || ""}</div></div></div>
    ${rec.evaluation || rec.completed_orders || rec.distance_km != null ? `<div class="meta" style="margin-bottom:10px">${rec.evaluation ? `${L.evaluation}: <b>${esc(rec.evaluation)}</b> &nbsp; ` : ""}${rec.completed_orders ? `${L.completedOrders}: <b>${esc(rec.completed_orders)}</b>` : ""}${rec.distance_km != null ? ` &nbsp; المسافة المستحقة: <b>${esc(rec.distance_km)} كم</b>` : ""}</div>` : ""}
    <div class="cols">
      <div class="col"><h3>${L.salaryAllowances}</h3><table>${rowsHtml(adds)}</table></div>
      <div class="col deds"><h3>${L.deductions}</h3><table>${rowsHtml(deds)}</table></div>
    </div>
    <div class="totals">
      <div class="tot g"><div class="lab">${L.totalSalary}</div><div class="num">${m(v.total_salary)}</div></div>
      <div class="tot n"><div class="lab">${L.netSalary}</div><div class="num">${m(v.net_salary)}</div></div>
    </div>
    <div style="display:flex;justify-content:flex-end;margin-top:26px">${officialSigBlock(official)}</div>
    <div class="ftr">${L.footerPrefix} ${new Date().toISOString().slice(0, 10)} — ${esc(companyName) || "WorkZone"}</div>
  </div><script>window.onload=()=>setTimeout(()=>window.print(),400)</script></body></html>`);
  w.document.close();
};

const Field = ({ label, value }) => (
  <div className="flex items-center justify-between border-b border-slate-100 py-2.5">
    <span className="text-sm text-slate-500">{label}</span>
    <span className="text-sm font-semibold text-slate-800">{value || "—"}</span>
  </div>
);

export default function EmployeePortal() {
  const { user, logout, platform } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => { await logout(); navigate("/employee", { replace: true }); };
  const { t, dir, lang } = useLang();
  const cur = t("portal.currency");
  const m = (n) => `${Number(n || 0).toLocaleString("en-US")} ${cur}`;
  const [data, setData] = useState(null);
  const [hasChat, setHasChat] = useState(false);
  useEffect(() => { api.portalCorrThreads().then((r) => setHasChat((r.items || []).length > 0)).catch(() => {}); }, []);
  const [bday, setBday] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [pwOpen, setPwOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailVal, setEmailVal] = useState("");
  const [tab, setTab] = useState(() => {
    const t = sessionStorage.getItem("portal_initial_tab");
    if (t) sessionStorage.removeItem("portal_initial_tab");
    return t || "performance";
  });
  const [navOpen, setNavOpen] = useState(false);
  useBackNav("portal_tab", tab, setTab);
  const [policyBusy, setPolicyBusy] = useState(false);
  const acceptPolicy = async () => {
    setPolicyBusy(true);
    try { await api.portalAcceptPolicy(); await load(); }
    catch (err) { toast.error(err?.response?.data?.detail || t("portal.loadFail")); }
    finally { setPolicyBusy(false); }
  };
  const openObjectionForm = () => window.open(OBJECTION_FORM_URL, "_blank", "noopener,noreferrer");

  const [photoBusy, setPhotoBusy] = useState(false);
  const photoRef = useRef(null);
  const onPhotoPick = async (ev) => {
    const f = ev.target.files?.[0];
    if (ev.target) ev.target.value = "";
    if (!f) return;
    setPhotoBusy(true);
    try { await api.portalUploadPhoto(f); toast.success(t("portal.more.photoSent")); await load(); }
    catch (err) { toast.error(err?.response?.data?.detail || t("portal.more.photoFail")); }
    finally { setPhotoBusy(false); }
  };

  const goToNearestStation = () => {
    const s = data?.company?.fuel_stations?.[Number(nearestStation)];
    if (!s) return;
    const q = encodeURIComponent((s.name || t("portal.misc.fuelStation")).trim());
    // Open the tab synchronously inside the tap, otherwise mobile browsers/apps block it.
    const go = (url, w) => { if (w && !w.closed) w.location.href = url; else window.location.assign(url); };
    if (s.map_url) { go(s.map_url, window.open(s.map_url, "_blank")); return; }
    const fallback = `https://www.google.com/maps/search/${q}`;
    const w = window.open("", "_blank");
    if (!navigator.geolocation) { go(fallback, w); return; }
    toast.info(t("portal.misc.locating"));
    navigator.geolocation.getCurrentPosition(
      (pos) => go(`https://www.google.com/maps/search/${q}/@${pos.coords.latitude},${pos.coords.longitude},14z`, w),
      () => { toast.warning(t("portal.misc.locateFail")); go(fallback, w); },
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 300000 }
    );
  };

  const [docBusy, setDocBusy] = useState(false);
  const [docKind, setDocKind] = useState("iqama");
  const docRef = useRef(null);
  const onDocPick = async (ev) => {
    const f = ev.target.files?.[0];
    if (ev.target) ev.target.value = "";
    if (!f) return;
    setDocBusy(true);
    try {
      const r = await api.portalUploadDocument(f, docKind);
      if (r?.moved_to) toast.warning(`هذه الاستمارة مفوّض عليها ${r.moved_to} — أُرسلت لملفه بانتظار موافقة الإدارة`, { duration: 8000 });
      else toast.success(`تم رفع الوثيقة${r?.expiry_date ? ` (تنتهي ${r.expiry_date})` : ""} — بانتظار موافقة الإدارة`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || t("portal.more.idFail")); }
    finally { setDocBusy(false); }
  };

  const [oilBusy, setOilBusy] = useState(false);
  const oilRef = useRef(null);
  const onOilPick = async (ev) => {
    const f = ev.target.files?.[0];
    if (ev.target) ev.target.value = "";
    if (!f) return;
    setOilBusy(true);
    try {
      const r = await api.portalOilChange(f);
      if (r.over_limit) toast.warning(r.message);
      else toast.success(r.message);
      await loadMaint();
    } catch (err) { toast.error(err?.response?.data?.detail || t("portal.maint.oilFail")); }
    finally { setOilBusy(false); }
  };

  const [invBusy, setInvBusy] = useState(false);
  const [invAmount, setInvAmount] = useState("");
  const invRef = useRef(null);
  const onInvoicePick = async (ev) => {
    const f = ev.target.files?.[0];
    if (ev.target) ev.target.value = "";
    if (!f) return;
    const amt = parseFloat(invAmount);
    if (!amt || amt <= 0) { toast.error(t("portal.maint.enterAmountFirst")); return; }
    setInvBusy(true);
    try {
      const r = await api.portalMaintenanceInvoice(f, amt, "");
      toast.success(r.message || t("portal.maint.invBtn"));
      setInvAmount("");
      await loadMaint();
    } catch (err) { toast.error(err?.response?.data?.detail || t("portal.maint.invFail")); }
    finally { setInvBusy(false); }
  };

  const load = useCallback(async () => {
    try { setData(await api.portalMe()); } catch { toast.error(t("portal.loadFail")); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => {
    setAutoOrg(data?.employee?.org_id);
    const b = data?.birthday;
    if (b?.is_birthday) {
      let seen = false;
      try { seen = !!sessionStorage.getItem(`bday_${b.birth_date}`); } catch { /* ignore */ }
      if (!seen) setBday(true);
    }
  }, [data]);

  const [viol, setViol] = useState({ items: [], summary: {} });
  const loadViol = useCallback(async () => {
    try { setViol(await api.portalViolations()); } catch { /* ignore */ }
  }, []);

  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const loadNotifs = useCallback(async () => {
    try { setNotifs(await api.portalNotifications()); } catch { /* ignore */ }
  }, []);
  const markAllRead = async () => { try { await api.portalNotificationsReadAll(); loadNotifs(); } catch { /* ignore */ } };

  const [perf, setPerf] = useState(null);
  const loadPerf = useCallback(async () => {
    try { setPerf(await api.portalPerformance()); } catch { /* ignore */ }
  }, []);
  const [fuel, setFuel] = useState(null);
  const [nearestStation, setNearestStation] = useState("");
  const loadFuel = useCallback(async () => {
    try { setFuel(await api.portalFuel()); } catch { /* ignore */ }
  }, []);
  const [maint, setMaint] = useState(null);
  const loadMaint = useCallback(async () => {
    try { setMaint(await api.portalMaintenance()); } catch { /* ignore */ }
  }, []);
  const [faqs, setFaqs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const loadFaqs = useCallback(async () => {
    try { const d = await api.portalFaqs(); setFaqs(d.faqs || []); } catch { /* ignore */ }
  }, []);
  const loadContacts = useCallback(async () => {
    try { const d = await api.portalContacts(); setContacts(d.contacts || []); } catch { /* ignore */ }
  }, []);
  const [salary, setSalary] = useState(null);
  const [salMonth, setSalMonth] = useState("");
  const loadSalary = useCallback(async (mo) => {
    try { const d = await api.portalSalary(mo); setSalary(d); if (!mo && d.month) setSalMonth(d.month); } catch { /* ignore */ }
  }, []);
  const [objOpen, setObjOpen] = useState(false);
  const [objReason, setObjReason] = useState("");
  const [objBusy, setObjBusy] = useState(false);
  const submitObjection = async () => {
    if (!objReason.trim()) { toast.error(t("portal.toast.writeObjection")); return; }
    setObjBusy(true);
    try {
      await api.portalObjectSalary(salary.record.id, objReason.trim());
      toast.success(t("portal.toast.objectionSent"));
      setObjOpen(false); setObjReason(""); loadSalary(salMonth || undefined);
    } catch (err) { toast.error(err?.response?.data?.detail || t("portal.toast.objectionFail")); }
    finally { setObjBusy(false); }
  };

  useEffect(() => { load(); loadViol(); loadNotifs(); loadPerf(); loadFuel(); loadMaint(); loadFaqs(); loadContacts(); }, [load, loadViol, loadNotifs, loadPerf, loadFuel, loadMaint, loadFaqs, loadContacts]);
  useEffect(() => {
    const id = setInterval(() => { loadNotifs(); }, 60000);
    return () => clearInterval(id);
  }, [loadNotifs]);
  useEffect(() => { loadSalary(salMonth || undefined); }, [salMonth, loadSalary]);

  const [bioPolicy, setBioPolicy] = useState(null);
  useEffect(() => { api.portalBioPolicy().then(setBioPolicy).catch(() => {}); }, []);
  const doCheck = async (action, notes = "") => {
    let bio = null;
    if (action !== "permission" && bioPolicy?.required) {
      if (!bioPolicy.has_passkey) { toast.error("الحضور يتطلب البصمة — فعّل البصمة من الإعدادات أولاً"); return; }
      setBusy(action);
      try { bio = await attendanceBio(); }
      catch (e) { setBusy(""); toast.error(e?.response?.data?.detail || "تم إلغاء البصمة أو فشلت"); return; }
    }
    setBusy(action);
    let geo = null;
    if (action === "check_in" || action === "check_out") {
      geo = await new Promise((res) => {
        if (!navigator.geolocation) return res(null);
        navigator.geolocation.getCurrentPosition((p) => res({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
          () => res(null), { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 });
      });
    }
    try {
      await api.portalCheck(action, notes, bio, geo);
      if (action === "check_in" || action === "check_out") sendLocation("attendance");
      toast.success(action === "check_in" ? t("portal.toast.checkInDone") : action === "check_out" ? t("portal.toast.checkOutDone") : t("portal.toast.permissionDone"));
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("portal.toast.actionFail")); }
    finally { setBusy(""); }
  };

  // ---- Advances ----
  const [reqOpen, setReqOpen] = useState(false);
  const [reqAmount, setReqAmount] = useState("");
  const [reqReason, setReqReason] = useState("");
  const [reqBusy, setReqBusy] = useState(false);
  const [blocked, setBlocked] = useState(null);
  const [cpNew, setCpNew] = useState("");
  const [cpNew2, setCpNew2] = useState("");
  const [cpCur, setCpCur] = useState("");
  const [cpBusy, setCpBusy] = useState(false);
  const [consentOk, setConsentOk] = useState(false);
  const mustChange = !!data?.employee?.must_change_password;
  const isSupervisor = !!data?.employee?.is_supervisor;
  const _emp = data?.employee || {};
  const _role = `${_emp.job_title || ""} ${_emp.profession || ""} ${_emp.department || ""}`.toLowerCase();
  const isLead = isSupervisor || ["مدير", "مشرف", "إداري", "اداري", "إدارة", "ادارة", "manager", "supervisor", "admin"].some((k) => _role.includes(k));
  const isSaudi = /سعود|saudi/i.test(_emp.nationality || "");
  const allowedTabs = PORTAL_TABS.filter((tb) => (tb.value === "library" ? isLead : tb.value === "chat" ? isLead || isSaudi || hasChat : true));
  const portalTabs = isSupervisor
    ? [allowedTabs[0], { value: "tasks", Icon: ClipboardList }, ...allowedTabs.slice(1)]
    : allowedTabs;

  const printCustodyReceipt = (c) => {
    const company = data?.company || {};
    const empName = data?.employee?.name || data?.birthday?.name || "";
    const asset = { id: c.id, type: c.type, identifier: c.identifier, branch: c.branch, fuel_code: c.fuel_code };
    const asg = { employee_name: empName, iqama_number: data?.employee?.iqama_number || "", branch: c.branch, received_at: c.since ? String(c.since).slice(0, 10) : "", returned_at: null, note: c.note || "", signature_id: c.signature_id, inspection: c.inspection, photos: c.photos };
    if (c.is_vehicle) printVehicleReceipt(asset, asg, company);
    else printReceipt(asset, asg, { company });
  };

  const submitChangePw = async () => {
    if (!/^\d{6}$/.test(cpNew || "")) { toast.warning(t("portal.more.pwMin")); return; }
    if (cpNew !== cpNew2) { toast.warning(t("portal.more.pwMismatch")); return; }
    setCpBusy(true);
    try {
      await api.portalChangePassword(cpCur, cpNew);
      toast.success(t("portal.more.pwUpdated"));
      setCpCur(""); setCpNew(""); setCpNew2("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("portal.more.pwFail")); }
    finally { setCpBusy(false); }
  };
  const [signAdv, setSignAdv] = useState(null);
  const [signBusy, setSignBusy] = useState(false);
  const sigRef = useRef(null);

  const submitRequest = async () => {
    if (!reqAmount || Number(reqAmount) <= 0) { toast.warning(t("portal.toast.enterValidAmount")); return; }
    if (!reqReason.trim()) { toast.warning(t("portal.toast.enterReason")); return; }
    setReqBusy(true);
    try {
      await api.portalRequestAdvance(Number(reqAmount), reqReason);
      toast.success(t("portal.toast.advanceSent"));
      setReqOpen(false); setReqAmount(""); setReqReason("");
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "advance_blocked") {
        setReqOpen(false);
        setBlocked(d);
      } else {
        toast.error((typeof d === "string" && d) || t("portal.toast.requestFail"));
      }
    }
    finally { setReqBusy(false); }
  };

  const submitSign = async () => {
    const sig = sigRef.current?.getSignature();
    if (!sig) { toast.warning(t("portal.toast.signFirst")); return; }
    setSignBusy(true);
    try {
      await api.portalSignAdvance(signAdv.id, sig, await optionalBio("advance_sign"));
      toast.success(t("portal.toast.signSent"));
      setSignAdv(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("portal.toast.signFail")); }
    finally { setSignBusy(false); }
  };

  // ---- Document preview ----
  const [preview, setPreview] = useState(null);
  const openPreview = async (d) => {
    if (!d.attachment_id) { toast.info(t("portal.toast.noAttachment")); return; }
    const name = (d.attachment_name || "").toLowerCase();
    try {
      const blob = await api.portalFileBlob(d.attachment_id);
      if (name.endsWith(".pdf") || (blob.type || "").includes("pdf") || (await blob.slice(0, 4).text()) === "%PDF") {
        const r = await api.portalPdfPages(d.attachment_id);
        setPreview({ label: docLabel(d), kind: "pdf", pages: r.pages || [], id: d.attachment_id });
      } else {
        setPreview({ label: docLabel(d), kind: "image", url: URL.createObjectURL(blob), id: d.attachment_id });
      }
    } catch { toast.error(t("portal.toast.openFail")); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400 font-body">{t("common.loading")}</div>;
  if (!data) return null;

  // Mandatory onboarding gates — strict sequence: (1) change password → (2) signature consent → (3) policies
  if (mustChange) {
    return (
      <div dir={dir} className="fixed inset-0 z-[95] flex flex-col bg-slate-900/95" data-testid="portal-must-change-gate">
        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          <div className="mx-auto my-2 max-w-lg rounded-3xl bg-white p-5 space-y-4 border-4 border-blue-500">
            <div className="text-center space-y-2">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-100"><KeyRound className="h-8 w-8 text-blue-600" /></div>
              <h2 className="font-heading text-lg font-extrabold text-slate-900">{t("portal.more.setNewPw")}</h2>
              <p className="text-xs text-slate-400">{t("portal.more.tempPwNote")}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">{t("portal.more.newPw")}</label>
              <input data-testid="cp-new" type="password" autoComplete="new-password" inputMode="numeric" pattern="[0-9]*" maxLength={6} dir="ltr" placeholder="6 أرقام" value={cpNew} onChange={(e) => setCpNew(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-3 text-base focus:border-blue-400 focus:outline-none" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">{t("portal.more.confirmNewPw")}</label>
              <input data-testid="cp-new2" type="password" autoComplete="new-password" inputMode="numeric" pattern="[0-9]*" maxLength={6} dir="ltr" placeholder="6 أرقام" value={cpNew2} onChange={(e) => setCpNew2(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={(e) => e.key === "Enter" && submitChangePw()}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-3 text-base focus:border-blue-400 focus:outline-none" />
            </div>
          </div>
        </div>
        <div className="shrink-0 border-t border-slate-200 bg-white px-4 pt-3" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 14px)" }}>
          <div className="mx-auto max-w-lg">
            <button data-testid="cp-submit" disabled={cpBusy} onClick={submitChangePw}
              className="w-full rounded-xl bg-blue-600 py-4 text-base font-bold text-white hover:bg-blue-700 disabled:opacity-60 flex items-center justify-center gap-2">
              {cpBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <KeyRound className="h-5 w-5" />} {t("portal.more.saveContinue")}
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (!consentOk) {
    return <SignatureConsentGate onCleared={() => setConsentOk(true)} />;
  }

  const policy = data.policy || {};
  if (policy.required) {
    const P = {
      title: { ar: "السياسات الداخلية للشركة", en: "Company Internal Policies", ur: "کمپنی کی داخلی پالیسیاں" },
      intro: { ar: "يرجى قراءة السياسات الداخلية للشركة والموافقة عليها للمتابعة إلى بوابتك.", en: "Please read and accept the company internal policies to continue to your portal.", ur: "براہ کرم جاری رکھنے کے لیے پالیسیاں پڑھ کر قبول کریں۔" },
      agree: { ar: "أوافق على السياسات وأدخل", en: "I agree — enter portal", ur: "میں متفق ہوں — داخل ہوں" },
      note: { ar: "بالضغط على «أوافق» تُحفظ نسخة من السياسات في وثائقك.", en: "By clicking Agree, a copy is saved to your documents.", ur: "قبول کرنے پر نقل آپ کی دستاویزات میں محفوظ ہو جائے گی۔" },
    };
    const pl = (k) => P[k][lang] || P[k].ar;
    return (
      <div dir={dir} className="min-h-screen bg-slate-100 font-body flex items-center justify-center p-4" data-testid="policy-gate">
        <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden flex flex-col max-h-[92vh]">
          <div className="p-5 border-b border-slate-100 bg-rose-50/60">
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-red-600" data-testid="policy-title">{pl("title")}</h1>
            <p className="text-sm text-slate-600 mt-1">{pl("intro")}</p>
          </div>
          <div className="p-5 overflow-y-auto flex-1 space-y-4">
            {policy.text ? (
              <div className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700" data-testid="policy-text">{policy.text}</div>
            ) : null}
            {policy.pdf_id ? (
              <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="policy-pdf">
                <iframe title="policy-pdf" src={api.portalFileUrl(policy.pdf_id)} className="w-full h-[55vh]" />
                <a href={api.portalFileUrl(policy.pdf_id)} target="_blank" rel="noopener noreferrer"
                  className="block text-center text-sm text-blue-600 py-2 hover:bg-slate-50" data-testid="policy-pdf-link">
                  {policy.pdf_name || "PDF"}
                </a>
              </div>
            ) : null}
          </div>
          <div className="p-5 border-t border-slate-100 bg-white">
            <p className="text-[11px] text-slate-400 mb-2 text-center">{pl("note")}</p>
            <Button onClick={acceptPolicy} disabled={policyBusy} data-testid="policy-accept-btn"
              className="w-full h-12 text-base bg-red-600 hover:bg-red-700 gap-2">
              {policyBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : null} {pl("agree")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const e = data.employee || {};
  const company = data.company || {};
  const att = data.attendance || {};
  const advances = data.advances || { items: [], outstanding: 0, pending: 0 };
  const today = att.today;
  const docs = Object.entries(e.documents || {}).map(([k, v]) => ({ key: k, ...v }));
  const ADMIN_ROLE_KEYWORDS = ["مدير", "مشرف", "إداري", "اداري", "إدارة", "ادارة", "manager", "supervisor", "admin"];
  const roleText = `${e.job_title || ""} ${e.profession || ""} ${e.department || ""}`.toLowerCase();
  const isAdminRole = !!e.is_supervisor || ADMIN_ROLE_KEYWORDS.some((k) => roleText.includes(k.toLowerCase()));
  const customDocs = (e.custom_documents || []).map((d, i) => ({ key: `c${i}`, ...d }));
  const docLabel = (d) => { const tr = t(`portal.docTypes.${d.key}`); return tr.startsWith("portal.") ? d.label : tr; };
  const allDocs = [...docs, ...customDocs].filter((d) => d.status !== "none" || d.expiry_date || d.attachment_id);
  const noneDocs = [...docs, ...customDocs].filter((d) => d.status === "none" && !d.expiry_date && !d.attachment_id);

  const initials = (e.name || "؟").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("");

  return (
    <div dir={dir} className="min-h-screen bg-slate-50 font-body" data-testid="employee-portal">
      <TargetWarningGate />
      {bday && data?.birthday && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center overflow-hidden bg-black/50 p-4" data-testid="birthday-overlay">
          <style>{`@keyframes bfloat{0%{transform:translateY(15vh);opacity:0}15%{opacity:1}100%{transform:translateY(-115vh);opacity:.85}}.b-balloon{position:absolute;bottom:-60px;font-size:40px;animation:bfloat linear infinite;pointer-events:none}`}</style>
          {["🎈","🎉","🎈","🎊","🎈","🎉","🎈","🎊","🎈"].map((e, i) => (
            <span key={i} className="b-balloon" style={{ left: `${6 + i * 10}%`, animationDuration: `${6 + (i % 4)}s`, animationDelay: `${i * 0.5}s` }}>{e}</span>
          ))}
          <div className="relative z-10 w-full max-w-sm rounded-3xl bg-white p-6 text-center shadow-2xl">
            <div className="text-6xl">🎂</div>
            <h2 className="mt-2 font-heading text-2xl font-extrabold text-blue-700">عيد ميلاد سعيد 🎉</h2>
            <p className="mt-1 text-lg font-bold text-slate-800">{data.birthday.name}</p>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              تتمنى لك <b className="text-slate-900">{data.birthday.company_name}</b> عامًا مليئًا بالصحة والسعادة والتوفيق. شكرًا لكونك جزءًا من عائلتنا 💐
            </p>
            <button data-testid="birthday-close"
              onClick={() => { try { sessionStorage.setItem(`bday_${data.birthday.birth_date}`, "1"); } catch { /* ignore */ } setBday(false); }}
              className="mt-5 h-11 w-full rounded-xl bg-blue-600 font-semibold text-white hover:bg-blue-700">شكرًا 🥳</button>
          </div>
        </div>
      )}
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-white/85 border-b border-slate-200/70">
        <div className="mx-auto max-w-3xl px-4 py-3 flex flex-wrap items-center gap-2">
          <button type="button" data-testid="portal-header-menu" aria-label="القائمة" onClick={() => setNavOpen(true)}
            className="order-0 h-11 w-11 shrink-0 rounded-2xl border border-slate-200 bg-white shadow-sm flex items-center justify-center transition-colors hover:bg-slate-50">
            <Menu className="h-5 w-5 text-slate-700" />
          </button>
          <div className="flex items-center gap-2.5 order-1 me-auto min-w-0">
            {company.logo_id ? (
              <img src={api.portalFileUrl(company.logo_id)} alt="" className="h-9 w-9 rounded-lg object-contain bg-white border border-slate-200 shrink-0" />
            ) : (
              <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center text-white shrink-0"><Building2 className="h-5 w-5" /></div>
            )}
            <div className="leading-tight min-w-0">
              <div className="font-heading font-bold text-slate-900 text-sm truncate">{company.name}</div>
              <div className="text-[11px] text-slate-400 truncate">{t("portal.header.portalOf")} · {platform?.name || "WorkZone"}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 order-3 sm:order-2 w-full sm:w-auto justify-end flex-wrap">
            <InstallAppButton compact />
            <PushToggle audience="employee" compact />
            <LanguageSwitcher />
            <DropdownMenu onOpenChange={(o) => { if (o && notifs.unread > 0) markAllRead(); }}>
              <DropdownMenuTrigger asChild>
                <button data-testid="portal-notif-bell" className="relative h-9 w-9 rounded-full border border-slate-200 bg-white flex items-center justify-center hover:bg-slate-50 transition">
                  <Bell className="h-4.5 w-4.5 text-slate-600" />
                  {notifs.unread > 0 && (
                    <span data-testid="portal-notif-badge" className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">{notifs.unread}</span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72 max-h-96 overflow-y-auto" dir={dir}>
                <DropdownMenuLabel>{t("portal.header.notifications")}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notifs.items.length === 0 ? (
                  <div className="py-6 text-center text-sm text-slate-400">{t("portal.header.noNotifications")}</div>
                ) : notifs.items.slice(0, 20).map((n) => (
                  <div key={n.id} data-testid={`portal-notif-${n.id}`} onClick={() => { if (n.type === "corr_message" || n.type === "corr_mention") setTab("chat"); }} className={`px-3 py-2.5 border-b border-slate-50 ${n.type === "corr_message" || n.type === "corr_mention" ? "cursor-pointer hover:bg-slate-50" : ""} ${!n.read ? "bg-blue-50/50" : ""}`}>
                    <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                      {n.type === "violation_added" ? <AlertTriangle className="h-3.5 w-3.5 text-rose-500" /> : n.type === "purchase" ? <Receipt className="h-3.5 w-3.5 text-blue-500" /> : <WalletIcon className="h-3.5 w-3.5 text-violet-500" />}
                      {n.title}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{n.body}</div>
                    <div className="text-[10px] text-slate-300 mt-1">{(n.created_at || "").slice(0, 16).replace("T", " ")}</div>
                  </div>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="order-2 sm:order-3">
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="portal-account-menu" className="flex items-center gap-2 rounded-full border border-slate-200 bg-white pl-1 pr-3 py-1 hover:bg-slate-50 transition">
                {data.top_performer?.is_top && <span title={t("portal.more.topMonthTitle")} data-testid="portal-top-badge-header" className="inline-flex items-center gap-1 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 text-white text-[10px] font-bold px-2 py-0.5"><Crown className="h-3 w-3" /> {t("portal.more.topMonthTitle")}</span>}
                <span className="text-sm font-semibold text-slate-700 max-w-[120px] truncate">{e.name}</span>
                <Avatar className="h-7 w-7">
                  {e.photo_id ? <AvatarImage src={api.portalFileUrl(e.photo_id)} /> : null}
                  <AvatarFallback className="bg-blue-100 text-blue-700 text-xs">{initials}</AvatarFallback>
                </Avatar>
                <ChevronDown className="h-4 w-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48" dir={dir}>
              <DropdownMenuItem data-testid="portal-change-password" onClick={() => setPwOpen(true)}>
                <KeyRound className="h-4 w-4 mx-2" /> {t("portal.header.changePassword")}
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="portal-logout" onClick={handleLogout} className="text-red-600">
                <LogOut className="h-4 w-4 mx-2" /> {t("portal.header.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
      </header>

      <AnnouncementTicker />

      <main className="mx-auto max-w-3xl px-4 py-6 space-y-6">
        {advances.awaiting_sign > 0 && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} data-testid="portal-approval-banner"
            className="rounded-2xl border-r-4 border-r-violet-500 bg-violet-50 p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 text-violet-800">
              <FileSignature className="h-5 w-5 shrink-0" />
              <span className="text-sm font-semibold">{t("portal.banner.awaitingSign", { n: advances.awaiting_sign })}</span>
            </div>
            <button data-testid="portal-goto-advances" onClick={() => setTab("advances")}
              className="rounded-lg bg-violet-600 text-white text-xs px-3 py-1.5 hover:bg-violet-700 shrink-0">{t("portal.banner.signNow")}</button>
          </motion.div>
        )}
        {/* Attendance quick actions — managers/supervisors/admin roles only */}
        {isAdminRole && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="portal-attendance-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-bold text-slate-900 flex items-center gap-2"><Clock className="h-5 w-5 text-blue-600" /> {t("portal.att.today")}</h2>
            <span className="text-xs text-slate-400">{new Date().toLocaleDateString("ar-SA")}</span>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-4 text-center text-sm">
            <div className="rounded-xl bg-slate-50 border border-slate-100 py-2.5">
              <div className="text-[11px] text-slate-400">{t("portal.att.status")}</div>
              <div className={`font-bold ${ATT_COLOR[today?.status] || "text-slate-400"}`}>{today ? t(`portal.attStatus.${today.status}`) : t("portal.att.notLogged")}</div>
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-100 py-2.5">
              <div className="text-[11px] text-slate-400">{t("portal.att.checkIn")}</div>
              <div className="font-bold font-mono-nums text-slate-800">{today?.check_in || "—"}</div>
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-100 py-2.5">
              <div className="text-[11px] text-slate-400">{t("portal.att.checkOut")}</div>
              <div className="font-bold font-mono-nums text-slate-800">{today?.check_out || "—"}</div>
            </div>
          </div>
          {today?.hours != null && (
            <div data-testid="portal-att-hours-today" className="mb-4 rounded-xl bg-blue-50 border border-blue-100 py-2.5 px-4 flex items-center justify-between">
              <span className="text-sm font-semibold text-blue-700 flex items-center gap-1.5"><Clock className="h-4 w-4" /> {t("portal.att.hoursToday")}</span>
              <span className="font-bold font-mono-nums text-blue-800 text-lg">{today.hours} {t("portal.att.hoursShort")}</span>
            </div>
          )}
          {bioPolicy?.required && (
            <div data-testid="portal-bio-required-note" className={`mb-3 rounded-xl border px-3 py-2 text-xs font-medium flex items-center gap-1.5 ${bioPolicy.has_passkey ? "bg-emerald-50 border-emerald-100 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
              <Fingerprint className="h-4 w-4" />
              {bioPolicy.has_passkey ? "تسجيل الحضور والانصراف يتم ببصمة جوالك" : "الحضور يتطلب البصمة — فعّل البصمة من الإعدادات أولاً"}
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            <Button data-testid="portal-check-in" disabled={busy || (today && today.check_in)} onClick={() => doCheck("check_in")}
              className="bg-emerald-600 hover:bg-emerald-700 gap-1.5">
              {busy === "check_in" ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />} {t("portal.att.btnIn")}
            </Button>
            <Button data-testid="portal-check-out" disabled={busy || !today?.check_in || (today && today.check_out)} onClick={() => doCheck("check_out")}
              className="bg-slate-800 hover:bg-slate-900 gap-1.5">
              {busy === "check_out" ? <Loader2 className="h-4 w-4 animate-spin" /> : <OutIcon className="h-4 w-4" />} {t("portal.att.btnOut")}
            </Button>
            <Button data-testid="portal-permission" disabled={busy} onClick={() => doCheck("permission")} variant="outline"
              className="gap-1.5 border-violet-200 text-violet-700 hover:bg-violet-50">
              {busy === "permission" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />} {t("portal.att.btnPermission")}
            </Button>
          </div>
        </motion.div>
        )}
        {isAdminRole && <PortalTeamAttendance />}

        <PortalLocationShare />
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          {/* Dropdown sections menu (like the company "الأقسام" drawer) — on all sizes */}
          <div className="space-y-2 sm:max-w-xs">
            <Sheet open={navOpen} onOpenChange={setNavOpen}>
              <SheetTrigger asChild>
                <button data-testid="portal-tab-select"
                  className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 h-12 shadow-sm font-heading font-bold text-slate-800 transition hover:border-blue-200">
                  <span className="flex items-center gap-2">
                    {(() => { const Cur = (PORTAL_TABS.find((x) => x.value === tab) || {}).Icon || User; return <Cur className="h-[18px] w-[18px] text-blue-600" />; })()}
                    {t(`portal.tabs.${tab}`)}
                  </span>
                  <Menu className="h-5 w-5 text-slate-500" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" dir={dir} className="w-[280px] p-0 flex flex-col gap-0 bg-gradient-to-b from-slate-900 to-slate-800 border-slate-700 text-slate-100" data-testid="portal-nav-drawer">
                <SheetHeader className="p-4 border-b border-white/10 text-right space-y-0">
                  <SheetTitle className="font-heading text-base text-white">{t("portal.menu")}</SheetTitle>
                  <SheetDescription className="sr-only">portal sections</SheetDescription>
                </SheetHeader>
                <nav className="flex-1 overflow-y-auto p-2 space-y-1">
                  {portalTabs.map((it) => {
                    const active = tab === it.value;
                    const Icon = it.Icon;
                    return (
                      <button key={it.value} data-testid={`portal-tab-opt-${it.value}`}
                        onClick={() => { setTab(it.value); setNavOpen(false); }}
                        className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm transition-colors ${active ? "bg-blue-600 text-white font-semibold shadow-lg shadow-blue-600/25" : "text-slate-300 hover:bg-white/10 hover:text-white"}`}>
                        <Icon className={`h-[18px] w-[18px] ${active ? "text-white" : "text-slate-400"}`} />
                        <span className="flex-1 text-right">{t(`portal.tabs.${it.value}`)}</span>
                        {active && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                      </button>
                    );
                  })}
                </nav>
              </SheetContent>
            </Sheet>
            {tab === PORTAL_TABS[0].value && (
              <button type="button" data-testid="portal-objection-link-mobile" onClick={openObjectionForm}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 font-semibold text-sm py-2.5 hover:bg-amber-100 transition">
                <ExternalLink className="h-4 w-4" /> {t("portal.objectionLink")}
              </button>
            )}
          </div>

          {/* Old tab row hidden — navigation is via the dropdown menu above */}
          <TabsList className="hidden">
            {portalTabs.map((it) => (
              <TabsTrigger key={it.value} value={it.value} data-testid={`portal-tab-${it.value}`}>
                <it.Icon className="h-4 w-4 mx-1.5" /> {t(`portal.tabs.${it.value}`)}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Profile */}
          <PhoneReminder phone={e.phone} onSaved={(ph) => setData((d) => (d ? { ...d, employee: { ...(d.employee || {}), phone: ph } } : d))} />
          <TabsContent value="profile" className="mt-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-4 pb-4 border-b border-slate-100">
                <div className="relative shrink-0">
                  <Avatar className="h-16 w-16">
                    {e.photo_id ? <AvatarImage src={api.portalFileUrl(e.photo_id)} /> : null}
                    <AvatarFallback className="bg-blue-100 text-blue-700 text-lg">{initials}</AvatarFallback>
                  </Avatar>
                  <input ref={photoRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="portal-photo-input" onChange={onPhotoPick} />
                  <button type="button" data-testid="portal-photo-btn" disabled={photoBusy} onClick={() => photoRef.current?.click()}
                    title={t("portal.more.changePhoto")}
                    className="absolute -bottom-1 -left-1 h-7 w-7 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-md ring-2 ring-white hover:bg-blue-700 transition disabled:opacity-60">
                    {photoBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
                  </button>
                </div>
                <div>
                  <div className="font-heading font-bold text-lg text-slate-900">{e.name}</div>
                  {e.photo_status === "pending" && (
                    <div data-testid="portal-photo-pending" className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold px-2.5 py-0.5">
                      <Clock className="h-3 w-3" /> {t("portal.more.photoPending")}
                    </div>
                  )}
                  {data.top_performer?.is_top ? (
                    <span data-testid="portal-top-badge" className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 text-white text-xs font-bold px-3 py-1 shadow-sm animate-pulse">
                      <Trophy className="h-3.5 w-3.5" /> {t("portal.more.topPerformer", { n: Number(data.top_performer.points || 0).toLocaleString("en-US") })}
                    </span>
                  ) : data.top_performer?.rank && data.top_performer.rank <= 3 ? (
                    <span data-testid="portal-rank-badge" className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold px-2.5 py-0.5">
                      <Trophy className="h-3 w-3" /> {t("portal.more.rankMonth", { n: data.top_performer.rank })}
                    </span>
                  ) : null}
                  <div className="text-sm text-slate-500">{e.profession || "—"} · {e.branch || "—"}</div>
                </div>
              </div>
              {data.top_performer?.is_top && (
                <button data-testid="portal-share-achievement"
                  onClick={() => shareAchievement({ name: e.name, points: data.top_performer.points, month: data.top_performer.month, company: data.company?.name })}
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-4 py-2 shadow-sm transition">
                  <Share2 className="h-4 w-4" /> {t("portal.more.shareWhatsapp")}
                </button>
              )}
              <div className="mt-2">
                <Field label={t("portal.profile.empNumber")} value={e.employee_number} />
                {e.rider_id ? <Field label={t("portal.profile.riderId")} value={e.rider_id} /> : null}
                <Field label={t("portal.profile.iqama")} value={e.iqama_number} />
                <Field label={t("portal.profile.nationality")} value={e.nationality} />
                <Field label={t("portal.profile.department")} value={e.department} />
                <Field label={t("portal.profile.jobTitle")} value={e.job_title} />
                <Field label={t("portal.profile.salary")} value={e.salary ? m(e.salary) : "—"} />
                <Field label={t("portal.profile.vehicle")} value={e.vehicle_number} />
                <Field label={t("portal.profile.fuelCode")} value={e.fuel_code} />
                <Field label={t("portal.profile.passport")} value={e.passport_number} />
                <Field label={t("portal.profile.hireDate")} value={e.hire_date} />
                <Field label={t("portal.profile.status")} value={e.employee_status} />
                <PortalPhoneCard phone={e.phone} onSaved={(ph) => setData((d) => (d ? { ...d, employee: { ...(d.employee || {}), phone: ph } } : d))} />
                <div className="flex items-center justify-between py-2.5 border-b border-slate-100">
                  <span className="text-sm text-slate-500">{t("portal.profile.email")}</span>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-semibold text-slate-800 truncate" dir="ltr">{e.portal_email || "—"}</span>
                    <button data-testid="portal-email-edit-btn" onClick={() => { setEmailVal(e.portal_email || ""); setEmailOpen(true); }}
                      className="shrink-0 text-emerald-600 hover:text-emerald-700 text-xs font-bold whitespace-nowrap">تغيير</button>
                  </div>
                </div>
                <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
                  <DialogContent data-testid="portal-email-dialog" className="max-w-sm" dir="rtl">
                    <DialogHeader>
                      <DialogTitle>تغيير البريد الإلكتروني</DialogTitle>
                      <DialogDescription>يُستخدم لاستعادة كلمة المرور عند نسيانها.</DialogDescription>
                    </DialogHeader>
                    <Input data-testid="portal-email-input" type="email" dir="ltr" value={emailVal}
                      onChange={(ev) => setEmailVal(ev.target.value)} placeholder="name@example.com" />
                    <DialogFooter>
                      <Button data-testid="portal-email-save-btn" disabled={busy === "email"} onClick={async () => {
                        setBusy("email");
                        try { await api.portalSetEmail(emailVal); toast.success("تم تحديث البريد الإلكتروني"); setEmailOpen(false); await load(); }
                        catch (er) { toast.error(er?.response?.data?.detail || "تعذّر التحديث"); }
                        finally { setBusy(""); }
                      }}>حفظ</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                <div className="flex items-center justify-between py-2.5">
                  <span className="text-sm text-slate-500">{t("portal.profile.totalAdvances")}</span>
                  <span className="text-sm font-bold text-rose-600 font-mono-nums">{m(advances.outstanding)}</span>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <PasskeySettingCard />
            </div>
          </TabsContent>

          {/* Documents */}
          <TabsContent value="mycard" className="mt-4" data-testid="portal-mycard-tab">
            <PortalRiderCard />
          </TabsContent>
          {(isLead || isSaudi || hasChat) && (
            <TabsContent value="chat" className="mt-4" data-testid="portal-chat-tab">
              <PortalChat />
            </TabsContent>
          )}
          <TabsContent value="decisions" className="mt-4" data-testid="portal-decisions-tab">
            <PortalLegalSummons />
            <PortalResignation />
            <PortalDecisions />
          </TabsContent>
          {isLead && (
            <TabsContent value="library" className="mt-4" data-testid="portal-library-tab">
              <PortalLibrary />
            </TabsContent>
          )}

          <TabsContent value="tasks" className="mt-4" data-testid="portal-tasks-tab">
            <PortalTasks botUsername={company.telegram_bot_username} hasChatId={!!e.telegram_chat_id} linkUrl={company.telegram_link_url} />
          </TabsContent>

          <TabsContent value="docs" className="mt-4 space-y-2">
            {company.doc_upload_enabled !== false && <div data-testid="portal-doc-upload-card" className="rounded-2xl border border-blue-200 bg-gradient-to-l from-blue-50 to-white p-4">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-blue-600/10 flex items-center justify-center shrink-0">
                  <CreditCard className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-slate-900 text-sm">{t("portal.more.idTitle")}</div>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                    {t("portal.more.idDesc")}
                  </p>
                  <p className="mt-1 text-[11px] text-amber-700">كل وثيقة ترفعها تُراجع وتُعتمد من الإدارة قبل اعتمادها في ملفك.</p>
                  <select data-testid="portal-doc-kind" value={docKind} onChange={(ev) => setDocKind(ev.target.value)}
                    className="mt-2 h-9 w-full max-w-xs rounded-md border border-slate-200 bg-white px-2 text-sm">
                    {[{ key: "iqama", label: "الإقامة" }, { key: "passport", label: "جواز السفر" }].map((d) => <option key={d.key} value={d.key}>{docLabel(d)}</option>)}
                  </select>
                  <input ref={docRef} type="file" accept="image/png,image/jpeg,image/webp,application/pdf"
                    className="hidden" data-testid="portal-doc-input" onChange={onDocPick} />
                  <Button data-testid="portal-doc-upload-btn" disabled={docBusy} onClick={() => docRef.current?.click()}
                    className="mt-2.5 h-9 bg-blue-600 hover:bg-blue-700 gap-1.5 text-sm">
                    {docBusy
                      ? <><Loader2 className="h-4 w-4 animate-spin" /> {t("portal.maint.reading")}</>
                      : <><Sparkles className="h-4 w-4" /> {t("portal.more.idBtn")}</>}
                  </Button>
                </div>
              </div>
            </div>}
            {(e.pending_documents || []).filter((r) => r.status === "pending" || r.status === "rejected").slice(-6).reverse().map((r) => (
              <div key={r.id} data-testid={`portal-doc-req-${r.id}`} className={`flex items-center justify-between rounded-xl border p-3 text-sm ${r.status === "pending" ? "border-amber-200 bg-amber-50" : "border-rose-200 bg-rose-50"}`}>
                <span className="font-semibold text-slate-800">{docLabel({ key: r.doc_key, label: r.label })}</span>
                <span className={`text-xs font-bold ${r.status === "pending" ? "text-amber-700" : "text-rose-700"}`}>
                  {r.status === "pending" ? "بانتظار موافقة الإدارة" : `مرفوضة${r.reject_reason ? `: ${r.reject_reason}` : ""} — أعد الرفع`}
                </span>
              </div>
            ))}
            {allDocs.length === 0 && noneDocs.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-12 text-center text-slate-400">{t("portal.docs.none")}</div>
            )}
            {allDocs.map((d) => {
              const ui = STATUS_UI[d.status] || STATUS_UI.none;
              return (
                <button key={d.key} data-testid={`portal-doc-${d.key}`} onClick={() => openPreview(d)}
                  className={`w-full text-start rounded-xl border ${ui.bg} p-3.5 flex items-center justify-between transition hover:brightness-95`}>
                  <div className="flex items-center gap-2.5">
                    <ui.Icon className={`h-5 w-5 ${ui.c}`} />
                    <div>
                      <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">{docLabel(d)}
                        {d.attachment_id && <Eye className="h-3.5 w-3.5 text-slate-400" />}</div>
                      <div className="text-xs text-slate-500">{d.expiry_date ? t("portal.docs.expires", { d: d.expiry_date }) : t("portal.docs.noDate")}</div>
                    </div>
                  </div>
                  <div className={`text-xs font-bold ${ui.c}`}>
                    {t(`portal.docStatus.${d.status || "none"}`)}{typeof d.days_left === "number" ? (d.days_left < 0 ? ` · ${t("portal.docs.sinceDays", { n: Math.abs(d.days_left) })}` : ` · ${t("portal.docs.daysLeft", { n: d.days_left })}`) : ""}
                  </div>
                </button>
              );
            })}
            {noneDocs.length > 0 && (
              <div className="pt-2">
                <div className="text-xs text-slate-400 mb-1.5">{t("portal.docs.unregistered")}</div>
                <div className="flex flex-wrap gap-1.5">
                  {noneDocs.map((d) => (
                    <span key={d.key} className="text-xs rounded-full bg-slate-100 text-slate-400 px-2.5 py-1">{docLabel(d)}</span>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Custody */}
          <TabsContent value="custody" className="mt-4 space-y-2">
            {(data.custody || []).length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-12 text-center text-slate-400">{t("portal.custody.none")}</div>
            ) : (
              data.custody.map((c, i) => (
                <div key={i} data-testid={`portal-custody-${i}`} className="rounded-xl border border-slate-200 bg-white p-3.5">
                 <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0"><Boxes className="h-5 w-5 text-blue-600" /></div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-800 truncate">{c.type}</div>
                      {c.identifier ? <div className="text-xs font-mono-nums text-slate-500 truncate" dir="ltr">{c.identifier}</div> : null}
                      <div className="text-[11px] text-slate-400 truncate">{c.branch || "—"}</div>
                      {c.is_vehicle && c.fuel_code ? (
                        <div className="mt-1 inline-flex items-center gap-1 rounded-md bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700" data-testid={`portal-custody-fuel-${i}`}>
                          <Fuel className="h-3 w-3" /> كود البنزين: <span className="font-mono-nums" dir="ltr">{c.fuel_code}</span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <div className="text-xs text-slate-400 whitespace-nowrap text-left">{c.since ? t("portal.custody.since", { d: String(c.since).slice(0, 10) }) : ""}</div>
                    <button onClick={() => printCustodyReceipt(c)} data-testid={`portal-custody-print-${i}`}
                      className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 transition-colors">
                      <Printer className="h-3 w-3" /> {c.is_vehicle && c.inspection ? "مستند التسليم" : "سند العهدة"}
                    </button>
                  </div>
                 </div>
                 {c.is_vehicle && c.inspection ? (
                   <div className="mt-3 border-t border-slate-100 pt-3 space-y-3" data-testid={`portal-handover-${i}`}>
                     <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-700">
                       <FileSignature className="h-4 w-4" /> مستند تسليم المركبة — إثبات الحالة عند الاستلام
                     </div>
                     {c.inspection.odometer != null && c.inspection.odometer !== "" ? (
                       <div className="text-[11px] text-slate-500">قراءة العدّاد: <b className="text-slate-700 font-mono-nums">{String(c.inspection.odometer)}</b> كم</div>
                     ) : null}
                     <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                       {itemsFor(c.inspection.kind).filter((it) => it.key in (c.inspection.checklist || {})).map((it) => (
                         <div key={it.key} className="flex items-center gap-1.5 text-[11px] text-slate-600">
                           <span className={c.inspection.checklist[it.key] ? "text-emerald-600 font-bold" : "text-rose-500 font-bold"}>{c.inspection.checklist[it.key] ? "✓" : "✗"}</span>
                           {it.label}
                         </div>
                       ))}
                     </div>
                     {(c.inspection.damages || []).length > 0 && (
                       <div className="space-y-2">
                         <CarDamageDiagram kind={c.inspection.kind} damages={c.inspection.damages} />
                         <ol className="list-decimal pr-5 space-y-0.5 text-[11px] text-slate-600">
                           {c.inspection.damages.map((d, di) => (<li key={di}>{d.note || "ضرر"}</li>))}
                         </ol>
                       </div>
                     )}
                     {c.inspection.note ? <div className="text-[11px] text-slate-600 bg-slate-50 border border-slate-100 rounded-lg p-2"><b>ملاحظة الفحص:</b> {c.inspection.note}</div> : null}
                     {(c.photos || []).filter((p) => p.file_id).length > 0 && (
                       <div className="grid grid-cols-3 gap-2">
                         {c.photos.filter((p) => p.file_id).map((p, pi) => (
                           <a key={pi} href={fileUrl(p.file_id)} target="_blank" rel="noreferrer">
                             <img src={fileUrl(p.file_id)} alt="" className="h-20 w-full object-cover rounded-lg border border-slate-200" />
                           </a>
                         ))}
                       </div>
                     )}
                     {c.signature_id ? (
                       <div className="flex items-center gap-2 text-[11px] text-slate-500">
                         توقيعك عند الاستلام: <img src={fileUrl(c.signature_id)} alt="توقيع" className="h-10 object-contain border-b border-slate-300" />
                       </div>
                     ) : null}
                   </div>
                 ) : null}
                </div>
              ))
            )}
          </TabsContent>

          {/* Advances */}
          <TabsContent value="advances" className="mt-4 space-y-3">
            <div className="rounded-2xl border border-slate-200 bg-gradient-to-l from-blue-50 to-white p-5 flex items-center justify-between">
              <div>
                <div className="text-xs text-slate-500">{t("portal.adv.totalOutstanding")}</div>
                <div className="font-heading text-2xl font-extrabold text-slate-900 font-mono-nums">{m(advances.outstanding)}</div>
                {advances.pending > 0 && <div className="text-[11px] text-amber-600 mt-1">{t("portal.adv.pending", { n: advances.pending })}</div>}
              </div>
              <Button data-testid="portal-request-advance" onClick={() => setReqOpen(true)} className="bg-blue-600 hover:bg-blue-700 gap-1.5">
                <Plus className="h-4 w-4" /> {t("portal.adv.request")}
              </Button>
            </div>
            {advances.items.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-10 text-center text-slate-400">{t("portal.adv.none")}</div>
            ) : advances.items.map((a) => (
              <div key={a.id} data-testid={`portal-advance-${a.id}`} className="rounded-xl border border-slate-200 bg-white p-3.5">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-heading font-bold text-slate-900 font-mono-nums">{m(a.amount)}</div>
                    <div className="text-xs text-slate-400">{a.reason || "—"} · {(a.created_at || "").slice(0, 10)}</div>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${ADV_STATUS_CLS[a.status]}`}>{t(`portal.advStatus.${a.status}`)}</span>
                </div>
                {a.status === "disbursed" && (
                  <div className="mt-2 text-xs text-slate-500">
                    {t("portal.adv.paid")}: <b className="font-mono-nums">{m(a.paid)}</b> · {t("portal.adv.remaining")}: <b className="font-mono-nums text-rose-600">{m(a.outstanding)}</b>
                    {Number(e.salary) > 0 && <span className="text-slate-400"> {t("portal.adv.ofSalary", { n: Math.round((a.outstanding / Number(e.salary)) * 100) })}</span>}
                  </div>
                )}
                {a.status === "rejected" && a.reject_reason && <div className="mt-1 text-xs text-rose-500">{t("portal.adv.reason")}: {a.reject_reason}</div>}
                {(a.attachments || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {a.attachments.map((f, i) => (
                      <a key={f.id} data-testid={`portal-advance-file-${a.id}-${i}`} href={api.portalFileUrl(f.id)} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 rounded bg-blue-50 text-blue-700 px-1.5 py-0.5 text-[11px] hover:bg-blue-100">
                        <Paperclip className="h-3 w-3" /> {f.name || t("portal.adv.attachment")}
                      </a>
                    ))}
                  </div>
                )}
                <div className="mt-2 flex items-center gap-2">
                  {a.status === "approved" && (
                    <Button data-testid={`portal-sign-${a.id}`} size="sm" onClick={() => setSignAdv(a)} className="bg-violet-600 hover:bg-violet-700 gap-1.5 h-8">
                      <FileSignature className="h-3.5 w-3.5" /> {t("portal.adv.signBtn")}
                    </Button>
                  )}
                  {(a.status === "signed" || a.status === "disbursed") && a.signature_id && (
                    <button data-testid={`portal-receipt-${a.id}`} onClick={() => printPortalReceipt(a, company.name, e.name, api.portalFileUrl(a.signature_id), { cur, dir, lang, noSig: t("portal.more.noSig"), title: t("portal.more.receiptTitle"), heading: t("portal.more.receiptHeading"), empName: t("portal.more.empName"), reason: t("portal.more.reason"), approvedDate: t("portal.more.approvedDate"), signedDate: t("portal.more.signedDate"), disbursedDate: t("portal.more.disbursedDate"), sigLabel: t("portal.more.sigLabel"), declaration: t("portal.more.declaration") }, company)}
                      className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-blue-600 border border-slate-200 rounded-lg px-2.5 py-1">
                      <Printer className="h-3.5 w-3.5" /> {t("portal.adv.printReceipt")}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </TabsContent>

          {/* Performance */}
          <TabsContent value="performance" className="mt-4">
            <RiderSalaryPreview />
            <PerformanceTab data={perf} loading={!perf} editable={false} showAdvances={false} celebrate celebrateId={e.id} />
          </TabsContent>

          {/* Violations */}
          <TabsContent value="violations" className="mt-4 space-y-2"><PortalKeetaViolations />            <div className="grid grid-cols-3 gap-2 mb-1">
              <div className="rounded-xl bg-white border border-slate-200 py-2.5 text-center"><div className="font-bold font-mono-nums text-slate-800">{viol.summary?.count ?? 0}</div><div className="text-[11px] text-slate-400">{t("portal.viol.count")}</div></div>
              <div className="rounded-xl bg-white border border-slate-200 py-2.5 text-center"><div className="font-bold font-mono-nums text-slate-800">{m(viol.summary?.total_amount)}</div><div className="text-[11px] text-slate-400">{t("portal.viol.total")}</div></div>
              <div className="rounded-xl bg-white border border-slate-200 py-2.5 text-center"><div className="font-bold font-mono-nums text-rose-600">{m(viol.summary?.unpaid)}</div><div className="text-[11px] text-slate-400">{t("portal.viol.unpaid")}</div></div>
            </div>
            {(viol.items || []).length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-10 text-center text-slate-400">{t("portal.viol.none")}</div>
            ) : viol.items.map((v) => (
              <div key={v.id} data-testid={`portal-violation-${v.id}`} className="rounded-xl border border-slate-200 bg-white p-3.5 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-800">{v.type}{v.points ? <span className="text-xs text-amber-600"> · {t("portal.viol.points", { n: v.points })}</span> : ""}</div>
                  <div className="text-xs text-slate-400" dir="ltr">
                    {v.violation_date_hijri ? `${v.violation_date_hijri} هـ` : (v.violation_date || "")}
                    {v.violation_date_hijri && v.violation_date ? ` · ${v.violation_date} م` : ""}
                    {v.location ? ` · ${v.location}` : ""}
                  </div>
                  {v.violation_number ? <div className="text-[11px] text-slate-400 font-mono-nums" dir="ltr">{t("portal.viol.violationNo")}: {v.violation_number}</div> : null}
                </div>
                <div className="flex items-center gap-2">
                  {v.photo_id && (
                    <button data-testid={`portal-violation-photo-${v.id}`} onClick={() => window.open(api.portalFileUrl(v.photo_id), "_blank")}
                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"><Eye className="h-3.5 w-3.5" /> {t("portal.viol.photo")}</button>
                  )}
                  <span className="font-mono-nums font-semibold text-slate-800">{m(v.amount)}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${v.status === "paid" ? "bg-emerald-100 text-emerald-700" : v.status === "deducted_unpaid" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>{v.status === "paid" ? t("portal.viol.paid") : v.status === "deducted_unpaid" ? t("portal.viol.deducted") : t("portal.viol.unpaid")}</span>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="accidents" className="mt-4">
            <PortalAccidents />
          </TabsContent>
          {/* Work injuries */}
          <TabsContent value="injuries" className="mt-4">
            <PortalInjuries />
          </TabsContent>

          {/* Attendance */}
          <TabsContent value="attendance" className="mt-4">
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3">
              {["present", "late", "permission", "leave", "absent"].map((k) => (
                <div key={k} className="rounded-xl bg-white border border-slate-200 py-2.5 text-center">
                  <div className={`font-bold font-mono-nums ${ATT_COLOR[k]}`}>{att.summary?.[k] ?? 0}</div>
                  <div className="text-[11px] text-slate-400">{t(`portal.attStatus.${k}`)}</div>
                </div>
              ))}
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
              <div className="px-4 py-2.5 border-b border-slate-100 text-sm font-semibold text-slate-700">{t("portal.att.thisMonthLog")}</div>
              {(att.records || []).length === 0 ? (
                <div className="py-10 text-center text-slate-400 text-sm">{t("portal.att.noRecords")}</div>
              ) : (
                <div className="divide-y divide-slate-50">
                  {att.records.map((r) => (
                    <div key={r.id || r.date} data-testid={`portal-att-${r.date}`} className="flex items-center justify-between px-4 py-2.5 text-sm">
                      <span className="font-mono-nums text-slate-600">{r.date}</span>
                      <span className={`font-semibold ${ATT_COLOR[r.status] || "text-slate-500"}`}>{t(`portal.attStatus.${r.status}`)}</span>
                      <span className="font-mono-nums text-slate-500 text-xs">{r.check_in || "—"} → {r.check_out || "—"}</span>
                      <span className="font-mono-nums text-slate-700 text-xs">{r.hours != null ? `${r.hours} ${t("portal.att.hoursShort")}` : "—"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="fuel" className="mt-4">
            {!fuel ? (
              <div className="py-12 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
            ) : (
              <div className="space-y-4" data-testid="portal-fuel">
                {fuel.fuel_cap && <FuelCapCard total={Number(fuel.total || 0)} fc={fuel.fuel_cap} cur={cur} />}
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                    <p className="text-xs text-emerald-700">{t("portal.fuel.totalYours", { m: fuel.month || "—" })}</p>
                    <p className="font-heading text-2xl font-bold text-emerald-700 font-mono-nums mt-1">{Number(fuel.total || 0).toLocaleString("en-US")} <span className="text-sm">{cur}</span></p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs text-slate-500">{t("portal.fuel.totalLiters")}</p>
                    <p className="font-heading text-2xl font-bold text-slate-800 font-mono-nums mt-1">{fuel.liters || 0}</p>
                  </div>
                </div>

                {(data.company?.fuel_stations || []).length > 0 && (
                  <div data-testid="portal-nearest-station" className="rounded-2xl border border-blue-100 bg-blue-50/50 p-4">
                    <p className="text-sm font-bold text-blue-700 flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {t("portal.misc.nearestStation")}</p>
                    <p className="text-xs text-slate-500 mb-2 mt-0.5">{t("portal.misc.nearestHint")}</p>
                    <div className="flex gap-2 items-center">
                      {(() => {
                        const sel = nearestStation !== "" ? data.company?.fuel_stations?.[Number(nearestStation)] : null;
                        return sel ? (
                          sel.logo_id
                            ? <img src={api.portalFileUrl(sel.logo_id)} alt="" className="h-10 w-10 rounded-lg object-contain bg-white border border-slate-200 shrink-0" />
                            : <span className="h-10 w-10 rounded-lg bg-blue-100 text-blue-700 font-bold flex items-center justify-center shrink-0">{(sel.name || "؟").charAt(0)}</span>
                        ) : null;
                      })()}
                      <Select value={nearestStation} onValueChange={setNearestStation}>
                        <SelectTrigger data-testid="nearest-station-select" className="flex-1 min-w-0 bg-white [&>span]:truncate [&>span]:min-w-0"><SelectValue placeholder={t("portal.misc.chooseStation")} /></SelectTrigger>
                        <SelectContent className="max-w-[calc(100vw-2rem)]">
                          {(data.company?.fuel_stations || []).map((s, i) => (
                            <SelectItem key={i} value={String(i)}>
                              <span className="flex items-center gap-2 min-w-0 whitespace-normal">
                                {s.logo_id
                                  ? <img src={api.portalFileUrl(s.logo_id)} alt="" className="h-5 w-5 rounded object-contain" />
                                  : <span className="h-5 w-5 shrink-0 rounded bg-blue-100 text-blue-700 text-[10px] font-bold flex items-center justify-center">{(s.name || "؟").charAt(0)}</span>}
                                <span className="truncate">{s.name}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {(() => {
                        return (
                          <Button data-testid="nearest-station-go" disabled={nearestStation === ""}
                            onClick={goToNearestStation}
                            className="bg-blue-600 hover:bg-blue-700 gap-1.5 shrink-0">
                            <Navigation className="h-4 w-4" /> {t("portal.misc.go")}
                          </Button>
                        );
                      })()}
                    </div>
                  </div>
                )}
                {fuel.days?.length ? (
                  <div className="space-y-2">
                    {fuel.days.map((d, i) => (
                      <div key={i} data-testid={`portal-fuel-day-${i}`} className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-3">
                        <span className="font-mono-nums text-sm font-bold text-emerald-700 whitespace-nowrap shrink-0">{Number(d.amount || 0).toLocaleString("en-US")} <span className="text-[10px] font-semibold">{cur}</span></span>
                        <div className="flex-1 min-w-0 text-center px-1">
                          <div className="text-xs text-slate-600 truncate">{d.station || "—"}</div>
                          <div className="text-[11px] text-slate-400 flex items-center justify-center gap-1"><Fuel className="h-3 w-3 shrink-0" />{d.liters || 0} {t("portal.fuel.litersShort")}</div>
                        </div>
                        <span className="font-mono-nums text-xs font-semibold text-slate-600 whitespace-nowrap shrink-0" dir="ltr">{d.date}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-10 text-center text-slate-400 text-sm">{t("portal.fuel.none")}</div>
                )}
              </div>
            )}
          </TabsContent>

          {/* My Maintenance (صيانتي) */}
          <TabsContent value="maintenance" className="mt-4 space-y-3" data-testid="portal-maintenance">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div data-testid="portal-oil-card" className="rounded-2xl border border-amber-200 bg-gradient-to-l from-amber-50 to-white p-4">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
                  <Droplets className="h-5 w-5 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-slate-900 text-sm">{t("portal.maint.oilTitle")}</div>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                    {t("portal.maint.oilDesc")}
                  </p>
                  <input ref={oilRef} type="file" accept="image/*" capture="environment"
                    className="hidden" data-testid="portal-oil-input" onChange={onOilPick} />
                  <Button data-testid="portal-oil-btn" disabled={oilBusy} onClick={() => oilRef.current?.click()}
                    className="mt-2.5 h-9 bg-amber-600 hover:bg-amber-700 gap-1.5 text-sm">
                    {oilBusy
                      ? <><Loader2 className="h-4 w-4 animate-spin" /> {t("portal.maint.reading")}</>
                      : <><Camera className="h-4 w-4" /> {t("portal.maint.oilBtn")}</>}
                  </Button>
                </div>
              </div>
            </div>

            <div data-testid="portal-invoice-card" className="rounded-2xl border border-emerald-200 bg-gradient-to-l from-emerald-50 to-white p-4">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                  <Receipt className="h-5 w-5 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-slate-900 text-sm">{t("portal.maint.invTitle")}</div>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                    {t("portal.maint.invDesc")}
                  </p>
                  <Input data-testid="portal-invoice-amount" type="number" inputMode="decimal" min="0" step="0.01"
                    value={invAmount} onChange={(e) => setInvAmount(e.target.value)}
                    placeholder={`${t("portal.maint.amount")} (${cur})`} className="mt-2.5 h-9 text-sm" />
                  <input ref={invRef} type="file" accept="image/*" capture="environment"
                    className="hidden" data-testid="portal-invoice-input" onChange={onInvoicePick} />
                  <Button data-testid="portal-invoice-btn" disabled={invBusy} onClick={() => invRef.current?.click()}
                    className="mt-2 h-9 bg-emerald-600 hover:bg-emerald-700 gap-1.5 text-sm w-full">
                    {invBusy
                      ? <><Loader2 className="h-4 w-4 animate-spin" /> {t("portal.maint.uploading")}</>
                      : <><Camera className="h-4 w-4" /> {t("portal.maint.invBtn")}</>}
                  </Button>
                </div>
              </div>
            </div>
            </div>

            {!maint?.vehicle_number ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-10 text-center text-slate-400 text-sm">
                {t("portal.maint.noVehicle")}
              </div>
            ) : (
              <>
                <div className="rounded-2xl border border-slate-200 bg-white p-3 flex items-center justify-between">
                  <div className="text-sm"><span className="text-slate-400">{t("portal.maint.myVehicle")}</span> <span className="font-mono-nums font-bold text-slate-800" dir="ltr">{maint.vehicle_number}</span></div>
                  <div className="text-xs text-slate-500">{t("portal.maint.limitEvery")} <span className="font-bold text-amber-600 font-mono-nums">{Number(maint.limit || 0).toLocaleString("en-US")}</span> {t("portal.maint.km")}</div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                  <div className="px-3 py-2.5 border-b border-slate-100 text-sm font-heading font-bold text-slate-800 flex items-center gap-1.5"><Droplets className="h-4 w-4 text-blue-600" /> {t("portal.maint.oilLog")}</div>
                  {(maint.oil_changes || []).length === 0 ? <p className="text-xs text-slate-400 p-4 text-center">{t("portal.maint.noRecord")}</p> : (
                    <div className="divide-y divide-slate-100">
                      {(maint.oil_changes || []).map((o) => (
                        <div key={o.id} className={`flex items-center justify-between gap-2 px-3 py-2.5 text-sm ${o.over_limit ? "bg-rose-50" : ""}`}>
                          <div><div className="font-mono-nums font-semibold text-slate-800">{Number(o.odometer).toLocaleString("en-US")} {t("portal.maint.km")}</div>
                            <div className="text-[11px] text-slate-400 font-mono-nums">{o.date}{o.km_since != null ? ` · ${t("portal.more.kmSince", { n: Number(o.km_since).toLocaleString("en-US") })}` : ""}</div></div>
                          <div className="flex items-center gap-2 shrink-0">
                            {o.over_limit && <span className="text-[11px] font-semibold text-rose-600 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> {t("portal.more.overLimit")}</span>}
                            {o.attachment_id && (
                              <ImageZoom src={api.fileViewUrl(o.attachment_id)} alt="صورة العدّاد"
                                label={`${Number(o.odometer).toLocaleString("en-US")} ${t("portal.maint.km")} · ${o.date}`}
                                testid={`portal-oil-photo-${o.id}`}
                                thumbClassName="h-11 w-14 object-cover rounded-md border border-slate-200 hover:ring-2 hover:ring-blue-400" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                  <div className="px-3 py-2.5 border-b border-slate-100 text-sm font-heading font-bold text-slate-800 flex items-center gap-1.5"><Wrench className="h-4 w-4 text-amber-600" /> {t("portal.maint.partsTitle")}</div>
                  {(maint.maintenance || []).length === 0 ? <p className="text-xs text-slate-400 p-4 text-center">{t("portal.maint.noRecord")}</p> : (
                    <div className="divide-y divide-slate-100">
                      {(maint.maintenance || []).map((m) => (
                        <div key={m.id} className="flex items-center justify-between px-3 py-2.5 text-sm">
                          <div><div className="font-semibold text-slate-800">{m.part_type}</div>
                            <div className="text-[11px] text-slate-400 font-mono-nums">{m.change_date}</div></div>
                          <div className="flex items-center gap-2">
                            {m.attachment_id && (
                              <a href={api.fileViewUrl(m.attachment_id)} target="_blank" rel="noopener noreferrer"
                                data-testid={`portal-maint-invoice-${m.id}`}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 hover:underline">
                                <Receipt className="h-3.5 w-3.5" /> {t("portal.maint.invoiceLink")}
                              </a>
                            )}
                            <div className="font-mono-nums text-slate-600">{Number(m.cost || 0).toLocaleString("en-US")} {cur}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </TabsContent>

          {/* Salary */}
          <TabsContent value="salary" className="mt-4">
            {!salary ? (
              <div className="py-12 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
            ) : (
              <div className="space-y-4" data-testid="portal-salary">
                {(salary.months || []).length > 0 && (
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <select data-testid="portal-salary-month" value={salMonth} onChange={(ev) => setSalMonth(ev.target.value)}
                      className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm">
                      {salary.months.map((mo) => <option key={mo} value={mo}>{mo}</option>)}
                    </select>
                    {salary.record?.is_freelance && (
                      <Button size="sm" data-testid="portal-freelance-invoice-btn" variant="outline"
                        onClick={() => issueAndPrintFreelance([{ ...salary.record, employee_name: e.name, iqama_number: e.iqama_number, rider_id: salary.record.rider_id || e.rider_id }], salary.fields || [], company, salary.record.month || salMonth, async ([id]) => ({ numbers: { [id]: (await api.portalFreelanceInvoice(id)).number } })).catch(() => toast.error("تعذّر إصدار الفاتورة"))}
                        className="gap-1.5 border-indigo-200 text-indigo-600 hover:bg-indigo-50">
                        <Printer className="h-4 w-4" /> فاتورة المستحقات
                      </Button>
                    )}
                    {salary.record && !salary.record.is_freelance && (
                      <Button size="sm" data-testid="portal-payslip-btn" variant="outline"
                        onClick={() => printPayslip(salary.record, salary.fields || [], company.name, e.name, { cur, dir, lang, title: t("portal.more.payslipTitle"), month: t("portal.more.payMonth"), evaluation: t("portal.more.evaluation"), completedOrders: t("portal.more.completedOrders"), salaryAllowances: t("portal.more.salaryAllowances"), deductions: t("portal.more.deductions"), totalSalary: t("portal.more.totalSalary"), netSalary: t("portal.more.netSalary"), footerPrefix: t("portal.more.payslipFooter") }, company)}
                        className="gap-1.5 border-blue-200 text-blue-600 hover:bg-blue-50">
                        <Printer className="h-4 w-4" /> {t("portal.salary.payslipBtn")}
                      </Button>
                    )}
                  </div>
                )}
                {!salary.record ? (
                  <div className="rounded-2xl border border-slate-200 bg-white py-12 text-center text-slate-400">{t("portal.salary.noRecord")}</div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
                        <p className="text-xs text-blue-700">{t("portal.salary.total", { m: salary.month })}</p>
                        <p className="font-heading text-2xl font-bold text-blue-700 font-mono-nums mt-1">{Number((salary.record.values || {}).total_salary || 0).toLocaleString("en-US")} <span className="text-sm">{cur}</span></p>
                      </div>
                      <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                        <p className="text-xs text-emerald-700">{t("portal.salary.net")}</p>
                        <p className="font-heading text-2xl font-bold text-emerald-700 font-mono-nums mt-1">{Number((salary.record.values || {}).net_salary || 0).toLocaleString("en-US")} <span className="text-sm">{cur}</span></p>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <p className="text-sm font-semibold text-slate-700 mb-2">{t("portal.salary.details")}</p>
                      {salary.record.distance_km != null && <p data-testid="portal-salary-km" className="mb-2 rounded-lg bg-sky-50 px-3 py-1.5 text-sm text-sky-800">المسافة المستحقة: <b className="font-mono-nums">{Number(salary.record.distance_km).toLocaleString("en-US")} كم</b></p>}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                        {(salary.fields || []).map((f) => (
                          <div key={f.key} className="flex items-center justify-between text-sm border-b border-slate-100 py-1.5">
                            <span className={f.group === "deduct" ? "text-rose-500" : f.group === "net" ? "font-semibold text-emerald-700" : f.group === "total" ? "font-semibold text-slate-700" : "text-slate-500"}>{f.label}</span>
                            <span className="font-mono-nums text-slate-700">{Number((salary.record.values || {})[f.key] || 0).toLocaleString("en-US")} {cur}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {(salary.record.invoice_penalties || []).length > 0 && (
                      <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-4" data-testid="portal-invoice-penalties">
                        <p className="text-sm font-semibold text-amber-800 mb-2">غرامات وخصومات فاتورة التطبيق</p>
                        {salary.record.invoice_penalties.map((x, i) => (
                          <div key={i} className="flex items-center justify-between text-sm border-b border-amber-100 py-1.5" data-testid={`portal-penalty-${i}`}>
                            <span className="text-rose-600">{x.label}</span>
                            <span className="font-mono-nums text-slate-700">{Number(x.amount || 0).toLocaleString("en-US")} {cur}</span>
                          </div>
                        ))}
                        <div className="flex items-center justify-between pt-2 text-sm font-semibold"><span>الإجمالي</span><span className="font-mono-nums text-rose-700">{salary.record.invoice_penalties.reduce((a, x) => a + Number(x.amount || 0), 0).toLocaleString("en-US")} {cur}</span></div>
                      </div>
                    )}

                    {/* Objection */}
                    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="portal-salary-objection">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-slate-700 flex items-center gap-2"><MessageSquareWarning className="h-4 w-4 text-amber-500" /> {t("portal.salary.objectionTitle")}</p>
                        {(!salary.objection || ["resolved", "rejected"].includes(salary.objection.status)) && (
                          <Button size="sm" data-testid="portal-salary-object-btn" onClick={() => setObjOpen(true)} variant="outline" className="gap-1.5 border-amber-200 text-amber-700 hover:bg-amber-50">
                            <MessageSquareWarning className="h-4 w-4" /> {t("portal.salary.submitObjection")}
                          </Button>
                        )}
                      </div>
                      {salary.objection ? (
                        <div className="mt-3 space-y-2">
                          <div className="flex items-center gap-2">
                            {(() => {
                              const map = { pending: ["bg-amber-50 text-amber-700 border-amber-200", Clock], reviewing: ["bg-sky-50 text-sky-700 border-sky-200", Clock], resolved: ["bg-emerald-50 text-emerald-700 border-emerald-200", Check], rejected: ["bg-rose-50 text-rose-700 border-rose-200", X] };
                              const st = salary.objection.status;
                              const [clsx, IconC] = map[st] || map.pending;
                              return <span data-testid="portal-objection-status" className={`inline-flex items-center gap-1 text-[11px] rounded-full border px-2 py-0.5 ${clsx}`}><IconC className="h-3 w-3" />{t(`portal.objStatus.${st}`)}</span>;
                            })()}
                            <span className="text-xs text-slate-400">{t("portal.salary.monthLabel", { m: salary.objection.month })}</span>
                          </div>
                          <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-2.5">{salary.objection.reason}</p>
                          {salary.objection.manager_response && <p className="text-xs text-slate-500 border-r-2 border-blue-300 pr-2">{t("portal.salary.managerResponse", { r: salary.objection.manager_response })}</p>}
                        </div>
                      ) : (
                        <p className="mt-2 text-xs text-slate-400">{t("portal.salary.objectionHint")}</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </TabsContent>

          {/* Leave */}
          <TabsContent value="leave" className="mt-4 space-y-4">
            <PortalFlights />
            <LeaveBoard mode="portal" />
          </TabsContent>

          {/* Loyalty */}
          <TabsContent value="loyalty" className="mt-4">
            <PortalLoyalty />
          </TabsContent>

          {/* Purchases */}
          <TabsContent value="purchases" className="mt-4">
            <PortalPurchases />
          </TabsContent>

          {/* My Wallet */}
          <TabsContent value="wallet" className="mt-4">
            <PortalWallet />
          </TabsContent>

          <TabsContent value="cash" className="mt-4">
            <PortalRiderCash />
          </TabsContent>

          {/* FAQ (الأسئلة الشائعة) */}
          <TabsContent value="faq" className="mt-4 space-y-3" data-testid="portal-faq">
            <div className="rounded-2xl border border-blue-200 bg-gradient-to-l from-blue-50 to-white p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                <HelpCircle className="h-5 w-5 text-blue-600" />
              </div>
              <div className="font-heading font-bold text-slate-900 text-sm">{t("portal.tabs.faq")}</div>
            </div>
            {faqs.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-10 text-center text-slate-400 text-sm">
                {t("portal.misc.faqEmpty")}
              </div>
            ) : (
              <Accordion type="single" collapsible className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden">
                {faqs.map((f) => (
                  <AccordionItem key={f.id} value={f.id} data-testid={`portal-faq-${f.id}`} className="border-0">
                    <AccordionTrigger className="px-4 py-3 text-sm font-semibold text-slate-800 hover:no-underline text-right">{f.question}</AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 text-sm text-slate-500 whitespace-pre-wrap leading-relaxed">{f.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </TabsContent>

          {/* Important contacts (أرقام تهمك) */}
          <TabsContent value="contacts" className="mt-4 space-y-3" data-testid="portal-contacts">
            <div className="rounded-2xl border border-emerald-200 bg-gradient-to-l from-emerald-50 to-white p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                <PhoneCall className="h-5 w-5 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <div className="font-heading font-bold text-slate-900 text-sm">{t("portal.tabs.contacts")}</div>
                <p className="text-xs text-slate-500 mt-0.5">{t("portal.misc.contactsHint")}</p>
              </div>
            </div>
            {contacts.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-10 text-center text-slate-400 text-sm">
                {t("portal.misc.contactsEmpty")}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {contacts.map((c) => (
                  <a key={c.id} href={`tel:${c.phone}`} data-testid={`portal-contact-${c.id}`}
                    className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 hover:border-emerald-300 hover:bg-emerald-50/40 transition active:scale-[0.99]">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-800 text-sm truncate">{c.name}</div>
                      <div className="text-sm text-emerald-600 font-mono-nums" dir="ltr">{c.phone}</div>
                      {c.note ? <div className="text-xs text-slate-400 mt-0.5 truncate">{c.note}</div> : null}
                    </div>
                    <div className="h-11 w-11 rounded-full bg-emerald-500 flex items-center justify-center shrink-0 shadow-sm">
                      <PhoneCall className="h-5 w-5 text-white" />
                    </div>
                  </a>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} portal />

      {/* Salary objection */}
      <Dialog open={objOpen} onOpenChange={setObjOpen}>
        <DialogContent className="sm:max-w-sm font-body" dir={dir} data-testid="portal-objection-dialog">
          <DialogHeader className={dir === "rtl" ? "text-right" : "text-left"}>
            <DialogTitle className="font-heading text-lg">{t("portal.salary.objectionTitle")}</DialogTitle>
            <DialogDescription className={dir === "rtl" ? "text-right" : "text-left"}>{t("portal.dlg.objDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>{t("portal.dlg.objReasonLabel")}</Label>
            <Textarea data-testid="portal-objection-reason" rows={4} value={objReason} onChange={(ev) => setObjReason(ev.target.value)} placeholder={t("portal.dlg.objPlaceholder")} />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setObjOpen(false)}>{t("portal.common.cancel")}</Button>
            <Button data-testid="portal-objection-submit" onClick={submitObjection} disabled={objBusy} className="bg-amber-600 hover:bg-amber-700">
              {objBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("portal.dlg.sendObjection")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request advance */}
      <Dialog open={reqOpen} onOpenChange={setReqOpen}>        <DialogContent className="sm:max-w-sm font-body" dir={dir} data-testid="portal-request-dialog">
          <DialogHeader className={dir === "rtl" ? "text-right" : "text-left"}>
            <DialogTitle className="font-heading text-lg">{t("portal.dlg.reqTitle")}</DialogTitle>
            <DialogDescription className={dir === "rtl" ? "text-right" : "text-left"}>{t("portal.dlg.reqDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>{t("portal.dlg.amountLabel")}</Label>
              <Input data-testid="portal-req-amount" type="number" dir="ltr" value={reqAmount} onChange={(ev) => setReqAmount(ev.target.value)} /></div>
            <div className="space-y-1.5"><Label>{t("portal.adv.reason")} *</Label>
              <Input data-testid="portal-req-reason" value={reqReason} onChange={(ev) => setReqReason(ev.target.value)} placeholder={t("portal.dlg.reasonRequired")} /></div>
          </div>
          <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
            <Button data-testid="portal-req-submit" disabled={reqBusy} onClick={submitRequest} className="bg-blue-600 hover:bg-blue-700 gap-1.5">
              {reqBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {t("portal.dlg.sendRequest")}
            </Button>
            <Button variant="outline" onClick={() => setReqOpen(false)}>{t("portal.common.cancel")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Advance blocked apology */}
      <Dialog open={!!blocked} onOpenChange={(o) => !o && setBlocked(null)}>
        <DialogContent className="sm:max-w-md font-body" dir={dir} data-testid="portal-advance-blocked-dialog">
          <DialogHeader className={dir === "rtl" ? "text-right" : "text-left"}>
            <div className="mx-auto mb-1 flex h-12 w-12 items-center justify-center rounded-full bg-rose-100">
              <AlertTriangle className="h-6 w-6 text-rose-600" />
            </div>
            <DialogTitle className="font-heading text-lg text-center">{t("portal.more.blockedTitle")}</DialogTitle>
            <DialogDescription className="text-center leading-relaxed text-slate-600" data-testid="portal-advance-blocked-msg">
              {blocked?.message}
            </DialogDescription>
          </DialogHeader>
          {blocked?.reason === "debt" && (
            <div className="rounded-xl bg-rose-50 border border-rose-100 p-3 text-sm text-rose-700 flex items-center justify-between" data-testid="portal-blocked-debt">
              <span>{t("portal.more.outstandingDebt")}</span>
              <span className="font-bold" dir="ltr">{m(blocked.outstanding)} / {m(blocked.limit)}</span>
            </div>
          )}
          {blocked?.reason === "target" && (
            <div className="rounded-xl bg-rose-50 border border-rose-100 p-3 text-sm text-rose-700 flex items-center justify-between" data-testid="portal-blocked-target">
              <span>{t("portal.more.lastDayOrders", { date: blocked.date })}</span>
              <span className="font-bold" dir="ltr">{blocked.completed} / {blocked.target}</span>
            </div>
          )}
          <DialogFooter className="sm:justify-center">
            <Button data-testid="portal-blocked-ok" onClick={() => setBlocked(null)} className="bg-blue-600 hover:bg-blue-700">
              {t("portal.common.close") || t("portal.more.ok")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sign receipt */}
      <Dialog open={Boolean(signAdv)} onOpenChange={(v) => !v && setSignAdv(null)}>
        <DialogContent className="sm:max-w-md font-body" dir={dir} data-testid="portal-sign-dialog">
          <DialogHeader className={dir === "rtl" ? "text-right" : "text-left"}>
            <DialogTitle className="font-heading text-lg">{t("portal.dlg.signTitle")}</DialogTitle>
            <DialogDescription className={dir === "rtl" ? "text-right" : "text-left"}>
              {t("portal.dlg.signDesc", { amt: m(signAdv?.amount) })}
            </DialogDescription>
          </DialogHeader>
          <SignaturePad ref={sigRef} />
          <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
            <Button data-testid="portal-sign-submit" disabled={signBusy} onClick={submitSign} className="bg-violet-600 hover:bg-violet-700 gap-1.5">
              {signBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4" />} {t("portal.dlg.approveSend")}
            </Button>
            <Button variant="outline" onClick={() => setSignAdv(null)}>{t("portal.common.cancel")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document preview */}
      <Dialog open={Boolean(preview)} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="sm:max-w-lg font-body max-h-[90vh] overflow-y-auto" dir={dir} data-testid="portal-preview-dialog">
          <DialogHeader className={dir === "rtl" ? "text-right pr-8" : "text-left pr-8"}>
            <DialogTitle className="font-heading text-lg">{preview?.label}</DialogTitle>
          </DialogHeader>
          {preview?.kind === "image" && <ZoomableView srcs={[preview.url]} maxH="max-h-[65vh]" />}
          {preview?.kind === "pdf" && (
            preview.pages.length === 0
              ? <p className="text-center text-slate-400 py-6">{t("portal.dlg.pdfFail")}</p>
              : <ZoomableView srcs={preview.pages} maxH="max-h-[65vh]" />
          )}
          {preview && (
            <a href={api.portalFileUrl(preview.id) + "?download=1"} className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline" data-testid="portal-preview-download">
              <FileText className="h-4 w-4" /> {t("portal.dlg.downloadOriginal")}
            </a>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
